import * as XLSX from 'xlsx';

/**
 * Validation result for Excel file processing
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  data?: any[];
}

/**
 * Maximum data rows in one campaign upload (BV-2739). Mirrors the server's
 * `campaign.max_records_per_upload` (service.DefaultCampaignMaxRecordsPerUpload);
 * change both together. The server is authoritative and rejects larger files too.
 */
export const MAX_CAMPAIGN_ROWS = 100;

/**
 * Validates that a campaign file stays within the row limit
 * @param rowCount - Number of data rows in the file (header and label rows excluded)
 * @param maxRows - Row limit to enforce
 * @returns Validation result with an error when the file has too many rows
 */
export const validateRowCount = (
  rowCount: number,
  maxRows = MAX_CAMPAIGN_ROWS,
): ValidationResult => {
  const errors: string[] = [];
  if (rowCount > maxRows) {
    errors.push(
      `This file has ${rowCount} rows. A campaign can have at most ${maxRows} rows — split the file and upload each part as its own campaign.`,
    );
  }
  return { isValid: errors.length === 0, errors, warnings: [] };
};

/**
 * Validates Excel file headers against expected headers
 * @param fileHeaders - Headers found in the Excel file
 * @param expectedHeaders - Headers expected based on workflow
 * @returns Validation result with errors and warnings
 */
export const validateHeaders = (
  fileHeaders: string[],
  expectedHeaders: string[],
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check for missing required headers
  const missingHeaders = expectedHeaders.filter(header => !fileHeaders.includes(header));

  if (missingHeaders.length > 0) {
    errors.push(`Missing required columns: ${missingHeaders.join(', ')}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Processes Excel data to match expected headers
 * @param jsonData - Raw JSON data from Excel
 * @param expectedHeaders - Expected headers to extract
 * @param startRowNumber - Excel row number that the first record in jsonData corresponds to.
 *   Defaults to 2 (row 1 = header). Set to 3 when a requirement-label row has been stripped.
 * @returns Processed data with cleaned values and row numbers
 */
export const processExcelData = (
  jsonData: any[],
  expectedHeaders: string[],
  startRowNumber = 2,
): any[] => {
  return jsonData.map((row, index) => {
    const processedRow: any = {};

    expectedHeaders.forEach(header => {
      // Get value from row, trim if it's a string
      const value = row[header];
      processedRow[header] = typeof value === 'string' ? value.trim() : value || '';
    });

    processedRow._rowNumber = index + startRowNumber;

    return processedRow;
  });
};

const REQUIREMENT_LABELS = new Set(['required', 'conditional', 'optional']);

/**
 * Detects the requirement-label row that the sample template generator writes immediately
 * below the header row (row 2 in a freshly downloaded template). Every non-empty cell must
 * be one of `required`/`conditional`/`optional` (case-insensitive). Empty rows do not count.
 */
export const isRequirementLabelRow = (row: Record<string, unknown>): boolean => {
  const values = Object.values(row)
    .map(v => (typeof v === 'string' ? v.trim().toLowerCase() : v))
    .filter(v => v !== '' && v !== null && v !== undefined);

  if (values.length === 0) return false;
  return values.every(v => typeof v === 'string' && REQUIREMENT_LABELS.has(v));
};

/**
 * Processes an Excel file and validates its contents
 * @param file - The Excel file to process
 * @param expectedHeaders - Expected headers based on workflow
 * @returns Promise with validation result and processed data
 */
export const processExcelFile = async (
  file: File,
  expectedHeaders: string[],
): Promise<ValidationResult> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    // Get the first worksheet
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // Convert to JSON
    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      raw: false,
      defval: '',
    });

    if (jsonData.length === 0) {
      return {
        isValid: false,
        errors: ['The Excel file is empty. Please upload a file with data.'],
        warnings: [],
      };
    }

    // Get headers from the Excel file
    const fileHeaders = Object.keys(jsonData[0] as object);

    // Validate headers
    const validation = validateHeaders(fileHeaders, expectedHeaders);

    if (validation.isValid) {
      // Strip the requirement-label row written by the sample generator (row 2 in a
      // freshly downloaded template). If the user has already deleted it, the data
      // is parsed starting at row 2 as before.
      const hasLabelRow = isRequirementLabelRow(jsonData[0] as Record<string, unknown>);
      const dataRows = hasLabelRow ? jsonData.slice(1) : jsonData;
      const startRowNumber = hasLabelRow ? 3 : 2;

      const rowLimit = validateRowCount(dataRows.length);
      if (!rowLimit.isValid) {
        return rowLimit;
      }

      const processedData = processExcelData(dataRows, expectedHeaders, startRowNumber);

      return {
        ...validation,
        data: processedData,
      };
    }

    return validation;
  } catch (error) {
    console.error('Error processing Excel file:', error);
    return {
      isValid: false,
      errors: ['Failed to process the Excel file. Please ensure it is a valid Excel file.'],
      warnings: [],
    };
  }
};

/**
 * Generates a default campaign name based on organization, program, and timestamp
 * @param orgName - Organization name
 * @param programName - Program name
 * @returns Generated campaign name
 */
export const generateDefaultCampaignName = (orgName?: string, programName?: string): string => {
  const now = new Date();

  // Format date and time in local timezone
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  const dateTimeStr = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;

  if (orgName && programName) {
    return `${orgName}_${programName}_${dateTimeStr}`;
  }

  // Fallback if org/program not available
  return `Campaign_${dateTimeStr}`;
};
